import type { FieldComponent } from '@atlas/react-forms';
import {
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'utils/translations';
import Errors from '../../Errors';
import Input from '../Input';

import styles from './PasswordField.module.scss';

const PasswordField: FieldComponent = ({
  onChange,
  field,
  onFocus,
  onBlur,
}) => {
  const [fieldType, setFieldType] = useState<'text' | 'password'>('password');
  const [showError, setShowError] = useState<boolean>(false);
  const fieldRef = useRef<HTMLInputElement>(null);

  const placeholder = useTranslation(field.label.toLowerCase()); // get a11y by field.label
  const initialValue = field.value?.toString() ?? '';
  const isValidField = !field.errors?.length;

  const handleChangeValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value, true);
    },
    [onChange],
  );

  const toggleShowPassword = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowError(false);
    setFieldType((prevFieldType) =>
      prevFieldType === 'password' ? 'text' : 'password',
    );
  };

  const handleBlur = () => {
    onBlur?.();
    setShowError(true);
  };

  const handleFocus = () => {
    onFocus?.();
    setShowError(false);
  };

  const focusInput = useCallback(() => {
    fieldRef?.current?.focus();
  }, []);

  const placeholderText = `${placeholder}${field.isRequired ? ' *' : ''}`;

  return (
    <div
      className={styles['form-field-container']}
      onClick={focusInput}
      onKeyDown={focusInput}
    >
      <Input
        ref={fieldRef}
        value={initialValue}
        onChange={handleChangeValue}
        type={fieldType}
        autoFocus={field.autofocus}
        name={field.name}
        id={field.name}
        onBlur={handleBlur}
        placeholder={placeholderText}
        onFocus={handleFocus}
        disabled={field.disabled || !field.isModifiable}
        isValid={isValidField}
        onIconClick={toggleShowPassword}
        iconName={fieldType === 'password' ? 'eye' : 'eye-slash'}
      />
      {showError && <Errors errors={field.errors} />}
    </div>
  );
};

export default PasswordField;
